import { AppSettingsService } from './app-settings.service';
import { CompanyInfo } from '../models/admin/company-info.model';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpParams } from '@angular/common/http';
import { HttpRequestService } from './http-request.service';
import { Injectable } from '@angular/core';
import { JwtModel } from '../models/admin/jwt.model';
import { JwtService } from './jwt.service';
import { LanguageService } from './language.service';
import { PanelProperty } from '../models/admin/panel-property.model';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../models/admin/user.model';
import { GroupInfo } from '../models/admin/group-info.model';
import { SidenavService } from 'src/app/layout/sidenav/sidenav.service';
@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private USER_MODEL = 'user';
  private REDIRECT_CCM = 'redirectCcm';
  private FILTER_DASHBOARD = 'filter';
  defaultAviPath = 'assets/img/avatars/default.png';

  constructor(
    private httpRequestService: HttpRequestService,
    private jwtService: JwtService,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private router: Router,
    private appSettingsService: AppSettingsService,
    private domSanitizer: DomSanitizer,
    private sidenavService: SidenavService,
  ) {}

  validateSession(encodedToken: string) {
    return this.httpRequestService.post<JwtModel>(this.appSettingsService.getApiEndpointAdmin(), `/Sessions`, {
      encodedToken: encodedToken,
    });
  }

  getUserInfo() {
    return this.httpRequestService.get<User>(this.appSettingsService.getApiEndpointAdmin(), '/User/GetUserInfo');
  }

  getCompanyGroups(companyId: number) {
    return this.httpRequestService.get<GroupInfo[]>(
      this.appSettingsService.getApiEndpointAdmin(),
      `/Companies/${companyId}/GetGroups`,
    );
  }

  getUserGroupOnDatabase() {
    return this.httpRequestService.get<GroupInfo>(this.appSettingsService.getApiEndpointAdmin(), '/User/GetGroup');
  }

  signout(): void {
    const isUserFromCcm = this.isUserFromCcm();
    const enableLoginOnLocalhost = this.appSettingsService.getLoginOnLocalhost();
    const params = new HttpParams()
      .set('token', this.jwtService.get().access_token)
      .set('client_id', isUserFromCcm ? 'hiperstream3' : 'datavalidationautomation')
      .set('client_secret', 'Dv4Ad1min2017*!')
      .set('type_credentials', 'client_credentials');
    this.httpRequestService
      .post<any>(this.appSettingsService.getProviderIdentity(), '/connect/revocation', params, false)
      .subscribe(() => {
        this.jwtService.delete();
        this.removeUser();
        this.removeFilter();
        if (isUserFromCcm) {
          window.location.href = this.appSettingsService.getHiperstream3();
        }
        if (enableLoginOnLocalhost === 'true') {
          window.location.reload();
        } else {
          window.location.href = this.appSettingsService.getHiperstream3();
        }
      });
  }

  private removeUser(): void {
    localStorage.removeItem(this.USER_MODEL);
    localStorage.removeItem(this.REDIRECT_CCM);
  }

  private removeFilter() {
    localStorage.removeItem(this.FILTER_DASHBOARD);
  }

  validateSessionWithSignin(encodedToken: string): void {
    this.validateSession(encodedToken).subscribe((token) => {
      this.signin(token);
    });
  }

  signin(token: JwtModel): void {
    this.jwtService.save(token);
    this.removeFilter();
    this.updateUser();
  }

  private updateUser(): void {
    this.getUserInfo().subscribe((user) => {
      if (user) {
        this.updateUserInStorage(user);
        this.setLanguageDefaultByUser(user);
        if (this.isUserFromCcm()) {
          window.location.reload();
        } else {
          this.router.navigate(['']);
          this.loadNavigation();
        }
      }
    });
  }

  loadNavigation() {
    this.sidenavService.addItems([
      {
        name: 'Menu.Dashboard',
        routeOrFunction: '/',
        icon: 'dashboard',
        position: 10,
        pathMatchExact: true,
      },
    ]);

    if (!this.isReadOnly()) {
      this.sidenavService.addItems([
        {
          name: 'Menu.Dashboard',
          routeOrFunction: '/',
          icon: 'dashboard',
          position: 10,
          pathMatchExact: true,
        },
        {
          name: 'Menu.Datasource',
          icon: 'storage',
          position: 15,
          subItems: [
            {
              name: 'Menu.Layouts',
              routeOrFunction: '/layout-manager',
              position: 10,
            },
            {
              name: 'Menu.AuxiliaryDatabase',
              routeOrFunction: '/auxiliary-database',
              position: 10,
            },
            {
              name: 'Menu.HistoricalData',
              routeOrFunction: '/historical-data',
              position: 15,
            },
          ],
        },
        {
          name: 'Menu.Rules',
          icon: 'rule',
          position: 20,
          subItems: [
            {
              name: 'Menu.Filters',
              routeOrFunction: '/filters',
              position: 10,
            },
            {
              name: 'Menu.Rules',
              routeOrFunction: '/rules',
              position: 15,
            },
            {
              name: 'Menu.DynamicFields',
              routeOrFunction: '/dynamic-fields',
              position: 20,
            },
            {
              name: 'Menu.ChecksReport',
              routeOrFunction: '/checks-report',
              position: 25,
            },
            {
              name: 'Menu.RulesClonesReport',
              routeOrFunction: '/rules-clones-report',
              position: 30,
            },
          ],
        },
        {
          name: 'Menu.Alerts',
          routeOrFunction: '/alerts',
          icon: 'notifications_active',
          position: 25,
          pathMatchExact: true,
        },
      ]);
      if (this.isMaster()) {
        this.sidenavService.addItem({
          name: 'Menu.Management',
          routeOrFunction: '/managements',
          icon: 'assignment',
          position: 30,
          pathMatchExact: true,
        });
      }
    }
  }

  getAccessLevel() {
    if (typeof this.getUserGroups() != 'undefined' && this.getUserGroups() != null) {
      return Number(
        this.getUserGroups().map(function (item, indice) {
          return item.accessLevel;
        }),
      );
    } else if (typeof this.getUserGroup() != 'undefined' && this.getUserGroup() != null) {
      return Number(this.getUserGroup().accessLevel);
    }
  }

  isReadOnly(): boolean {
    const accessLevel = this.getAccessLevel();
    if (accessLevel == 10) {
      return true;
    }
    return false;
  }

  private updateUserInStorage(user: User) {
    localStorage.setItem(this.USER_MODEL, JSON.stringify(user));
  }

  private setLanguageDefaultByUser(user: User) {
    if (user.languageAlias) {
      this.setLanguageDefault(user.languageAlias);
    }
  }

  setLanguageDefault(language: string) {
    this.languageService.defaultLanguage.next(language);
    this.translateService.use(this.languageService.defaultLanguage.getValue());
  }

  getUser(): User {
    const user = localStorage.getItem(this.USER_MODEL);
    if (user) {
      let parsedUser = JSON.parse(user) as User;
      return parsedUser;
    } else {
      return null;
    }
  }

  getUserAvi() {
    const user = localStorage.getItem(this.USER_MODEL);
    if (user) {
      let parsedUser = JSON.parse(user) as User;
      if (!parsedUser.base64Avatar) return this.defaultAviPath;
      return this.domSanitizer.bypassSecurityTrustResourceUrl(parsedUser.base64Avatar);
    } else {
      return null;
    }
  }

  updateUserAvi(encodedFile: string) {
    const user = localStorage.getItem(this.USER_MODEL);
    if (user) {
      let parsedUser = JSON.parse(user) as User;
      parsedUser.base64Avatar = encodedFile;
      localStorage.setItem(this.USER_MODEL, JSON.stringify(parsedUser));
      return this.httpRequestService.put(this.appSettingsService.getApiEndpointAdmin(), '/User/SaveAvatar', {
        base64Avatar: encodedFile,
      });
    } else {
      return null;
    }
  }

  getUserId() {
    const user = this.getUser();
    if (user != null) {
      return user.userId;
    }
    return null;
  }

  getCompanyId() {
    const user = this.getUser();
    if (user != null) {
      return user.companyId;
    }
    return null;
  }

  getTags() {
    const user = this.getUser();
    if (user != null && user.groupTags != null) {
      return user.groupTags;
    }
    return [];
  }

  getCompanies() {
    const user = this.getUser();
    if (user != null) {
      return user.companies;
    }
    return null;
  }

  getUserGroups() {
    const user = this.getUser();
    if (user != null) {
      return user.userGroups;
    }
    return null;
  }

  getUserGroup() {
    const user = this.getUser();
    if (user != null) {
      return user.userGroup;
    }
    return null;
  }

  getGroupsId() {
    const user = this.getUser();
    if (user != null) {
      return user.groupId;
    }
    return null;
  }

  updateUserGroups(groups: GroupInfo) {
    const user = this.getUser();

    user.groupId = groups.id;
    user.groupName = groups.name;
    user.groupAccessLevel = groups.accessLevel;
    user.groupCompanyId = groups.companyId;
    user.groupTags = groups.tags;
    user.groupRules = groups.rules;
    this.updateUserInStorage(user);
    this.removeFilter();

    window.location.reload();
  }

  isUserFromCcm() {
    const user = this.getUser();
    if (user != null) {
      return user.hp3;
    }
    return this.isRedirectFromCcm();
  }

  setRedirectFromCcm() {
    localStorage.setItem(this.REDIRECT_CCM, 'true');
  }

  private isRedirectFromCcm() {
    const redirectCcm = localStorage.getItem(this.REDIRECT_CCM);
    if (redirectCcm) {
      return redirectCcm === 'true';
    }
    return false;
  }

  getLanguageId() {
    const user = this.getUser();
    if (user != null) {
      return user.languageId;
    }
    return null;
  }

  getLanguageAlias(): string {
    const user = this.getUser();
    if (user != null) {
      return user.languageAlias;
    }
    return null;
  }

  getPropertiesOfProcessedRules() {
    const user = this.getUser();
    if (user != null) {
      return user.propertiesOfProcessedRules;
    }
    return null;
  }

  getNameProcessedRulesByProperty(property: string) {
    const user = this.getUser();
    if (user != null) {
      const propertyOfProcessedRulesFound = user.propertiesOfProcessedRules.find(
        (propertyOfProcessedRules) => propertyOfProcessedRules.property === property,
      );
      return propertyOfProcessedRulesFound?.name ?? property;
    }
    return property;
  }

  disableInitialMessage(companyId: number) {
    this.httpRequestService
      .put<any>(this.appSettingsService.getApiEndpointAdmin(), `Companies/${companyId}/DisableInitialMessage`)
      .subscribe(() => {
        const user = this.getUser();
        user.isDisplayInitialMessage = false;
        localStorage.setItem(this.USER_MODEL, JSON.stringify(user));
      });
  }

  isMaster(): boolean {
    const user = this.getUser();
    if (user != null) {
      return user.isMaster;
    }
    return false;
  }

  updateUserGroupTagsAndRules(groupId: string, tags: number[], rules: number[]) {
    const user = this.getUser();
    let groupIdFind = user.userGroups.findIndex((x) => x.id == parseInt(groupId));
    user.userGroups[groupIdFind].tags = tags.map(Number);
    user.userGroups[groupIdFind].rules = rules.map(Number);
    this.updateUserInStorage(user);
  }

  updateUserCompany(company: CompanyInfo) {
    const user = this.getUser();

    if (typeof user.userGroups != 'undefined' && user.userGroups != null) {
      this.getCompanyGroups(company.companyId).subscribe((groups) => {
        user.companyId = company.companyId;
        user.companyName = company.companyName;
        user.companyParentId = company.companyParentId;
        user.companyParentName = company.companyParentName;
        user.token = company.token;
        user.isMaster = company.isMaster;
        user.userGroups = groups;
        this.updateUserInStorage(user);
        this.removeFilter();
        window.location.reload();
      });
    } else if (typeof user.userGroup != 'undefined' && user.userGroup != null) {
      this.getUserGroupOnDatabase().subscribe((group) => {
        user.companyId = company.companyId;
        user.companyName = company.companyName;
        user.companyParentId = company.companyParentId;
        user.companyParentName = company.companyParentName;
        user.token = company.token;
        user.isMaster = company.isMaster;
        user.userGroup = group;
        this.updateUserInStorage(user);
        this.removeFilter();
        window.location.reload();
      });
    }
  }

  updatePropertiesOfProcessedRules(newPropertiesOfProcessedRules: PanelProperty[]) {
    const user = this.getUser();

    user.propertiesOfProcessedRules.forEach((propertyOfProcessedRules) => {
      const newPropertyOfProcessedRulesFound = newPropertiesOfProcessedRules.find(
        (newPropertyOfProcessedRules) => newPropertyOfProcessedRules.property === propertyOfProcessedRules.property,
      );
      if (newPropertyOfProcessedRulesFound) propertyOfProcessedRules.name = newPropertyOfProcessedRulesFound.name;
    });

    this.updateUserInStorage(user);
    window.location.reload();
  }
}
